import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/rootReducer";
import { useEffect, useState } from "react";
import {
  Link,
  Pagination,
  Table,
  TableProps,
  Container,
  Header,
  Alert,
  CollectionPreferences,
  Spinner,
  Icon,
  Button,
} from "@amzn/awsui-components-react";
import { FormField } from "@amzn/awsui-components-react/polaris";
import { getMarketplaceId } from "../../from-server/acs/acs-service";
import { decryptCustomerId } from "@amzn/amazon-id";
import { AuthorizedAttribute } from "../../from-server/brass/model";
import { fetchEditableAttributes } from "../porAsinList/porAsinListSlice";
import {
  setAsinMarketplaceSelectionLinks,
  setStamperWizardState,
} from "./stamperWizardConnector";
import { getLiveness } from "../app/api";
import { trackComponentView } from "../telemetry/telemetry";

export interface AsinMarketplaceSelectionLinks {
  asin: string;
  marketplace: string;
  liveness: string;
  waitingForLiveness: boolean;
  selectionLink: string;
}

export const StamperTable = () => {
  const dispatch = useDispatch();
  const {
    asinsMarketplaceLiveness,
    asinsWithListOfMarketplaces,
    touched,
    program,
    partiness,
    type,
  } = useSelector((state: RootState) => state.stamperWizard);
  const { editableAttributes, isGettingEditableAttributes } = useSelector(
    (state: RootState) => state.porAsinList
  );
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [asinMarketplaceSelectionLinks, setAsinMarketplaceSelectionLinksState] =
    useState<AsinMarketplaceSelectionLinks[]>([]);
  const participationString =
    !program || !partiness || !type
      ? undefined
      : `c2l-v1-${program}|${partiness}|${type}`;

  useEffect(() => {
    trackComponentView("stamper", "stampToCatalogTable");
  }, []);

  useEffect(() => {
    if (!isGettingEditableAttributes) {
      dispatch(fetchEditableAttributes());
    }
  });

  useEffect(() => {
    const temp = asinsWithListOfMarketplaces
      .map((asinMp) => {
        const asin = asinMp.asin;
        return asinMp.marketplaces.map((mp: any) => {
          if (`${asinMp.asin}#${mp}#stamp` in asinsMarketplaceLiveness) {
            return {
              asin: asin,
              marketplace: mp,
              liveness:
                asinsMarketplaceLiveness[`${asinMp.asin}#${mp}#stamp`].liveness,
              waitingForLiveness:
                asinsMarketplaceLiveness[`${asinMp.asin}#${mp}#stamp`]
                  .waitingForLiveness,
            } as AsinMarketplaceSelectionLinks;
          } else {
            dispatch(
              setStamperWizardState({
                item: `${asinMp.asin}#${mp}#stamp`,
                value: { liveness: "", waitingForLiveness: false },
              })
            );
            return {
              asin: asin,
              marketplace: mp,
              liveness: "",
              waitingForLiveness: false,
              selectionLink: mp,
            } as AsinMarketplaceSelectionLinks;
          }
        });
      })
      .flat();
    setAsinMarketplaceSelectionLinksState(temp);
    dispatch(
      setAsinMarketplaceSelectionLinks({ asinMarketplaceSelectionLinks: temp })
    );
  }, [asinsWithListOfMarketplaces, asinsMarketplaceLiveness]);

  function userIsAuthorized(editableAttributes: AuthorizedAttribute[]) {
    return editableAttributes.some(
      (attribute) =>
        attribute.attributeName === "publish_stamp" && attribute.authorized
    );
  }

  function retrieveLiveness(item: AsinMarketplaceSelectionLinks) {
    const marketNumber = Number(
      decryptCustomerId(getMarketplaceId(item.marketplace) || "")
    );
    dispatch(
      setStamperWizardState({
        item: `${item.asin}#${item.marketplace}#stamp`,
        value: { liveness: "", waitingForLiveness: true },
      })
    );
    getLiveness(item.asin, marketNumber)
      .then((result) => {
        dispatch(
          setStamperWizardState({
            item: `${item.asin}#${item.marketplace}#stamp`,
            value: { liveness: result.liveStatus, waitingForLiveness: false },
          })
        );
      })
      .catch(() => {
        dispatch(
          setStamperWizardState({
            item: `${item.asin}#${item.marketplace}#stamp`,
            value: { liveness: "error", waitingForLiveness: false },
          })
        );
      });
  }

  const columnDefinitions: TableProps.ColumnDefinition<AsinMarketplaceSelectionLinks>[] =
    [
      {
        id: "asin",
        header: "ASIN",
        cell: (item) => item.asin,
      },
      {
        id: "marketplace",
        header: "Marketplace",
        cell: (item) => item.marketplace,
      },
      {
        id: "liveness",
        header: "Liveness",
        width: "20%",
        cell: (item) => {
          if (item.liveness === "" && !item.waitingForLiveness) {
            retrieveLiveness(item);
          }
          return item.waitingForLiveness ? (
            <Spinner></Spinner>
          ) : item.liveness == "error" ? (
            <span>
              <Icon size="small" variant="error" name="status-negative" />{" "}
              <Button
                onClick={() => {
                  retrieveLiveness(item);
                }}
              >
                Retry
              </Button>
            </span>
          ) : (
            <span>{item.liveness}</span>
          );
        },
      },
      {
        id: "link",
        header: "Selection Central Link",
        cell: (item) => {
          const linkString = `https://selection.amazon.com/item/${decryptCustomerId(
            getMarketplaceId(item.marketplace) || ""
          )}/${item.asin}`;
          return (
            <>
              <div>
                <Link
                  external
                  externalIconAriaLabel="Opens in a new tab"
                  href={linkString}
                >
                  Selection Central
                </Link>
              </div>
            </>
          );
        },
      },
    ];
  return (
    <>
      <FormField stretch={true}>
        <Alert statusIconAriaLabel="Info">
          Verify using the provided Selection Central Link that the ASINs are
          created in the selected Marketplaces and all Catalog issues have been
          resolved. Once completed, click submit to stamp ASINs. Stamp will only
          be submitted to ASINs that are created in selected Marketplaces.
          Catalog issues on ASINs could block reconciliation. We cannot
          guarantee reconciliation will be successful in Catalog.
        </Alert>
        <Table
          header={
            <Header variant={"h3"}>Added ASINs (Stamp to Catalog)</Header>
          }
          footer={<br></br>}
          variant={"borderless"}
          enableKeyboardNavigation={true}
          columnDefinitions={columnDefinitions}
          contentDensity="compact"
          items={asinMarketplaceSelectionLinks.slice(
            pageNumber * pageSize - pageSize,
            pageNumber * pageSize
          )}
          pagination={
            <Pagination
              currentPageIndex={pageNumber}
              pagesCount={Math.ceil(
                asinMarketplaceSelectionLinks.length / pageSize
              )}
              onChange={({ detail }) => setPageNumber(detail.currentPageIndex)}
            />
          }
          preferences={
            <CollectionPreferences
              onConfirm={(event) => {
                setPageSize(event.detail.pageSize || 10);
              }}
              title="Preferences"
              confirmLabel="Confirm"
              cancelLabel="Cancel"
              preferences={{
                pageSize: pageSize,
              }}
              pageSizePreference={{
                title: "Page size",
                options: [
                  { value: 10, label: "10 records" },
                  { value: 20, label: "20 records" },
                  { value: 50, label: "50 records" },
                ],
              }}
            />
          }
        />
      </FormField>
    </>
  );
};
